/**
 * Measurement hook for synchronization verification.
 *
 * This is a test seam, not analytics: it never sends anything anywhere, keeps
 * a bounded in-memory buffer, and only reads state the player already has. It
 * is installed on `window.__watchalong` so a browser driver (or a person in
 * devtools) can sample two real clients and compare their positions *at a
 * common server instant* — the method required by
 * plan/03-synchronized-player.md and documented in docs/synchronization.md.
 *
 *   __watchalong.sample()          // one sample, right now
 *   __watchalong.start(250)        // begin recording at a cadence in ms
 *   __watchalong.stop()            // stop, return the recorded samples
 *   __watchalong.csv()             // recorded samples as CSV
 */

import type { SyncMeasurement } from './controller.svelte';
import type { RoomState } from '../room/state.svelte';

export interface SyncSample extends SyncMeasurement {
  /** Room epoch as a decimal string (the wire type is a `u64`). */
  readonly epoch: string | null;
  readonly revision: string | null;
  readonly clockOffsetMs: number | null;
  readonly clockRttMs: number | null;
  readonly clockQuality: string | null;
}

export interface SyncProbe {
  sample: () => SyncSample;
  start: (cadenceMs?: number) => void;
  stop: () => SyncSample[];
  recorded: () => SyncSample[];
  csv: () => string;
}

declare global {
  interface Window {
    __watchalong?: SyncProbe;
  }
}

/** 10 minutes at the 250 ms target cadence is 2 400 samples; this is slack. */
const MAX_SAMPLES = 20_000;

const CSV_COLUMNS: readonly (keyof SyncSample)[] = [
  'clientMs',
  'serverMs',
  'positionMs',
  'expectedMs',
  'driftMs',
  'rate',
  'state',
  'roomState',
  'generation',
  'ready',
  'epoch',
  'revision',
  'clockOffsetMs',
  'clockRttMs',
  'clockQuality',
];

export function installSyncProbe(room: RoomState): void {
  let samples: SyncSample[] = [];
  let timer: number | undefined;

  const take = (): SyncSample => {
    const base = room.player.sample();
    const snapshot = room.snapshot;
    const clock = room.clock;
    return {
      ...base,
      epoch: snapshot === null ? null : snapshot.epoch.toString(),
      revision: snapshot === null ? null : snapshot.revision.toString(),
      clockOffsetMs: clock?.offsetMs ?? null,
      clockRttMs: clock?.rttMs ?? null,
      clockQuality: clock?.quality ?? null,
    };
  };

  window.__watchalong = {
    sample: take,
    start: (cadenceMs = 250) => {
      clearInterval(timer);
      samples = [];
      timer = setInterval(() => {
        if (samples.length >= MAX_SAMPLES) {
          clearInterval(timer);
          timer = undefined;
          return;
        }
        samples.push(take());
      }, cadenceMs);
    },
    stop: () => {
      clearInterval(timer);
      timer = undefined;
      return samples;
    },
    recorded: () => samples,
    csv: () => {
      const rows = samples.map((sample) =>
        CSV_COLUMNS.map((column) => String(sample[column] ?? '')).join(','),
      );
      return [CSV_COLUMNS.join(','), ...rows].join('\n');
    },
  };
}
